import { useState, useCallback, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, getDefaultSchedule } from "@finitless/shared/lib";
import { useToastContext } from "@finitless/shared/contexts";
import { useUserStore } from "@finitless/shared/stores";
import { useBusinessGallery, useCities } from "./";

/**
 * Hook for business creation functionality
 * Handles form state, validation, and API calls for creating a new business
 */
export const useBusinessCreation = () => {
  const { success, error, loading, dismissAll } = useToastContext();
  const queryClient = useQueryClient();
  const { handleAddBusinessGallery } = useBusinessGallery();
  const { firstCityId } = useCities();
  const user = useUserStore((state) => state.user);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    address: "",
    country_phone_code: "",
    cellphone: "",
    email: "",
    location: {
      lat: null,
      lng: null,
      zipcode: -1,
      zoom: 15,
    },
    timezone: "",
    price_level: "1",
    logo: null,
    header: null,
    schedule: [],
    gallery: [],
    enabled: true,
    minimum: 0,
    delivery_price: 0,
    tax: 0,
    tax_type: 1,
    service_fee: 0,
  });

  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [isAddressSelected, setIsAddressSelected] = useState(false);

  // Auto-fill email with logged-in user's email when the hook first mounts
  useEffect(() => {
    if (user?.email && !formData.email) {
      setFormData((prev) => ({
        ...prev,
        email: user.email,
      }));
    }
  }, [user?.email, formData.email]);

  // Create business mutation
  const createBusinessMutation = useMutation({
    mutationFn: async (businessData) => {
      // Show loading toast
      loading("Creating business...");

      const response = await apiClient.post("/business", businessData);
      return response;
    },
    onSuccess: (data) => {
      // Dismiss any loading toasts
      dismissAll();

      success("Business created successfully!");
      // Invalidate businesses list (sidebar, brand selector, etc.)
      queryClient.invalidateQueries({
        queryKey: ["businesses"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["brand-businesses"],
        exact: false,
      });
      // Invalidate /menu/products list (uses businesses-products; has refetchOnMount: false)
      queryClient.removeQueries({ queryKey: ["businesses-products"] });
      return data;
    },
    onError: (err) => {
      // Dismiss any loading toasts
      dismissAll();

      // Show error message
      error(`Failed to create business: ${err.message}`);
      throw err;
    },
  });

  // Update form data
  const updateFormData = useCallback((updates) => {
    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  // Update specific field
  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Update nested field (e.g., location)
  const updateNestedField = useCallback((parentField, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value,
      },
    }));
  }, []);

  // Handle address selection from Google Maps
  const handleAddressSelection = useCallback((addressData) => {
    const { address, location, map_data, timezone, phone, price_level } =
      addressData;

    setFormData((prev) => ({
      ...prev,
      address: address || prev.address,
      location: {
        lat: location?.lat || prev.location.lat,
        lng: location?.lng || prev.location.lng,
        zipcode: addressData?.zipcode || prev.location.zipcode,
        zoom: 15,
      },
      timezone: timezone || prev.timezone,
      cellphone: phone || prev.cellphone,
      price_level: price_level ? price_level.toString() : prev.price_level,
    }));

    setSelectedPlaceId(map_data?.place_id);
    setIsAddressSelected(!!map_data?.place_id);
  }, []);

  // Generate slug from name using the project's stringToSlug function
  const generateSlug = (name) => {
    name = name.replace(/^\s+|\s+$/g, ""); // trim
    name = name?.toLowerCase();

    // remove accents, swap ñ for n, etc
    const from = "åàáãäâèéëêìíïîòóöôùúüûñç·/_,:;";
    const to = "aaaaaaeeeeiiiioooouuuunc------";

    for (let i = 0, l = from.length; i < l; i++) {
      name = name.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
    }

    name = name
      .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
      .replace(/\s+/g, "_") // collapse whitespace and replace by _
      .replace(/-+/g, "_") // collapse dashes
      .replace(/^-+/, "") // trim - from start of text
      .replace(/-+$/, ""); // trim - from end of text

    return name;
  };

  // Update name and auto-generate slug
  const updateName = (name) => {
    const slug = generateSlug(name);
    updateFormData({
      name,
      slug,
    });
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = "Business name is required";
    }

    if (!formData.slug?.trim()) {
      errors.slug = "Slug is required";
    }

    if (!formData.address?.trim()) {
      errors.address = "Address is required";
    }

    if (!formData.location.lat || !formData.location.lng) {
      errors.location = "Valid location is required";
    }

    if (!formData.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Valid email is required";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  // Create business
  const createBusiness = async (additionalData = {}) => {
    const validation = validateForm();

    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors).join(", ");
      error(`Validation failed: ${errorMessages}`);
      return { success: false, errors: validation.errors };
    }

    try {
      // Use schedule from form data, fallback to default if empty
      const defaultSchedule = getDefaultSchedule();

      const defaultAddBusinessParams = {
        minimum: 0,
        delivery_price: 0,
        tax: 0,
        tax_type: 1,
        service_fee: 0,
        enabled: true,
        owner_id: user?.id,
      };

      const businessData = {
        ...defaultAddBusinessParams,
        ...formData,
        ...additionalData,
        location: JSON.stringify(formData.location),
        schedule: JSON.stringify(
          formData.schedule.length > 0 ? formData.schedule : defaultSchedule,
        ),
        ...(firstCityId && { city_id: firstCityId }),
        ...(additionalData.logo?.base64 && {
          logo: additionalData.logo.base64,
        }),
        ...(additionalData.header?.base64 && {
          header: additionalData.header.base64,
        }),
        ...(additionalData.payment_methods && {
          payment_methods: additionalData.payment_methods,
        }),
      };

      // Remove null/undefined values
      Object.keys(businessData).forEach((key) => {
        if (businessData[key] === null || businessData[key] === undefined) {
          delete businessData[key];
        }
      });

      const result = await createBusinessMutation.mutateAsync(businessData);

      if (
        additionalData.gallery &&
        additionalData.gallery.length > 0 &&
        result?.content?.result?.id
      ) {
        try {
          await Promise.all(
            additionalData.gallery.map(async (galleryItem) => {
              const newGallery = {
                business_id: result.content.result.id,
                file: galleryItem.base64,
                type: 1,
              };
              return await handleAddBusinessGallery(
                result.content.result.id,
                newGallery,
              );
            }),
          );
        } catch {
          console.log("Error uploading gallery images");
        }
      }

      return { success: true, data: result };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Reset form - keeps user's email as default
  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      address: "",
      country_phone_code: "",
      cellphone: "",
      email: user?.email || "",
      location: {
        lat: null,
        lng: null,
        zipcode: -1,
        zoom: 15,
      },
      timezone: "",
      price_level: "1",
      logo: null,
      header: null,
      schedule: [],
      gallery: [],
      enabled: true,
      minimum: 0,
      delivery_price: 0,
      tax: 0,
      tax_type: 1,
      service_fee: 0,
    });
    setSelectedPlaceId(null);
    setIsAddressSelected(false);
  };

  return {
    // State
    formData,
    selectedPlaceId,
    isAddressSelected,
    isLoading: createBusinessMutation.isPending,

    // Actions
    updateFormData,
    updateField,
    updateNestedField,
    updateName,
    handleAddressSelection,
    createBusiness,
    resetForm,
    validateForm,

    // Validation
    validation: validateForm(),
  };
};

export default useBusinessCreation;
